import React, { useState } from "react";
import { Link } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import {
	MdOutlineBuild as WrenchIcon,
	MdOutlineChatBubbleOutline as ChatBubbleLeftIcon,
	MdOutlinePerson as UserIcon,
	MdOutlineFavoriteBorder as HeartIcon,
} from "react-icons/md";

const Footer: React.FC = () => {
	const currentYear = new Date().getFullYear();
	const [email, setEmail] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [subscriptionStatus, setSubscriptionStatus] = useState<
		"idle" | "success" | "error"
	>("idle");

	const handleNewsletterSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!email.trim()) return;

		setIsSubmitting(true);
		try {
			await addDoc(collection(db, "newsletterSubscriptions"), {
				email: email.trim(),
				subscribedAt: serverTimestamp(),
				isActive: true,
			});
			setSubscriptionStatus("success");
			setEmail("");
		} catch (error) {
			console.error("Error subscribing to newsletter:", error);
			setSubscriptionStatus("error");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<footer className="hidden md:block bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-t border-slate-700/50">
			<div className="container mx-auto px-4 py-16">
				{/* Main Footer Content */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
					{/* Brand Section */}
					<div className="space-y-6">
						<Link to="/" className="flex items-center space-x-3 group">
							<div className="relative">
								<WrenchIcon className="w-8 h-8 text-cyan-400 group-hover:text-cyan-300 transition-colors duration-200" />
								<div className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
							</div>
							<span className="text-white text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
								תיקנתי
							</span>
						</Link>
						<p className="text-slate-300 text-sm leading-relaxed">
							הפלטפורמה המובילה בישראל לקהילת ה-DIY. מצאו מדריכים, שתפו ידע
							והצטרפו למהפכת התיקונים.
						</p>
						<div className="flex space-x-6">
							<a
								href="https://www.linkedin.com/in/nir-hochman/"
								target="_blank"
								rel="noopener noreferrer"
								className="w-8 h-8 bg-slate-700 hover:bg-cyan-500 rounded-lg flex items-center justify-center transition-colors duration-200 group"
								title="LinkedIn Profile"
							>
								<svg
									className="w-4 h-4 text-slate-300 group-hover:text-white"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
								</svg>
							</a>
							<a
								href="https://buymeacoffee.com/nir.hochman"
								target="_blank"
								rel="noopener noreferrer"
								className="w-8 h-8 bg-slate-700 hover:bg-cyan-500 rounded-lg flex items-center justify-center transition-colors duration-200 group"
								title="Buy me a coffee"
							>
								<svg
									className="w-4 h-4 text-slate-300 group-hover:text-white"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<path d="M18.5 3H6c-1.1 0-2 .9-2 2v5.71c0 3.83 2.95 7.18 6.78 7.29 3.96.12 7.22-3.06 7.22-7v-1h.5c1.38 0 2.5-1.12 2.5-2.5S19.88 3 18.5 3zM16 5v5.61c0 2.96-2.3 5.47-5.27 5.38-2.78-.08-4.73-2.35-4.73-5.08V5h10zM20 8h-2V5c0-.55-.45-1-1-1s-1 .45-1 1v3h-2c-.55 0-1 .45-1 1s.45 1 1 1h6c.55 0 1-.45 1-1s-.45-1-1-1z" />
								</svg>
							</a>
						</div>
					</div>

					{/* Quick Links */}
					<div>
						<h3 className="text-white font-semibold text-lg mb-6">
							קישורים מהירים
						</h3>
						<ul className="space-y-4">
							<li>
								<Link
									to="/guides"
									className="text-slate-300 hover:text-cyan-400 transition-colors duration-200 flex items-center space-x-2"
								>
									<WrenchIcon className="w-4 h-4" />
									<span>מדריכים</span>
								</Link>
							</li>
							<li>
								<Link
									to="/forum"
									className="text-slate-300 hover:text-cyan-400 transition-colors duration-200 flex items-center space-x-2"
								>
									<ChatBubbleLeftIcon className="w-4 h-4" />
									<span>פורום</span>
								</Link>
							</li>
							<li>
								<Link
									to="/join"
									className="text-slate-300 hover:text-cyan-400 transition-colors duration-200 flex items-center space-x-2"
								>
									<UserIcon className="w-4 h-4" />
									<span>הקהילה</span>
								</Link>
							</li>
						</ul>
					</div>

					{/* Community */}
					<div>
						<h3 className="text-white font-semibold text-lg mb-6">הקהילה</h3>
						<ul className="space-y-4">
							<li>
								<Link
									to="/join"
									className="text-slate-300 hover:text-cyan-400 transition-colors duration-200"
								>
									הצטרף אלינו
								</Link>
							</li>
							<li>
								<Link
									to="/writing-guide"
									className="text-slate-300 hover:text-cyan-400 transition-colors duration-200"
								>
									מדריכי כתיבה
								</Link>
							</li>
							<li>
								<Link
									to="/affiliate-guide"
									className="text-slate-300 hover:text-orange-400 transition-colors duration-200"
								>
									מדריך שיווק שותפים
								</Link>
							</li>
							<li>
								<Link
									to="/rewards"
									className="text-slate-300 hover:text-cyan-400 transition-colors duration-200"
								>
									תגמולים
								</Link>
							</li>
							<li>
								<Link
									to="/leaderboard"
									className="text-slate-300 hover:text-cyan-400 transition-colors duration-200"
								>
									לוח תוצאות
								</Link>
							</li>
						</ul>
					</div>

					{/* Support */}
					<div>
						<h3 className="text-white font-semibold text-lg mb-6">תמיכה</h3>
						<ul className="space-y-4">
							<li>
								<Link
									to="/contact"
									className="text-slate-300 hover:text-cyan-400 transition-colors duration-200"
								>
									צור קשר
								</Link>
							</li>
							<li>
								<Link
									to="/bug-report"
									className="text-slate-300 hover:text-cyan-400 transition-colors duration-200"
								>
									דיווח על באג
								</Link>
							</li>
							<li>
								<Link
									to="/suggestions"
									className="text-slate-300 hover:text-cyan-400 transition-colors duration-200"
								>
									הצעות לשיפור
								</Link>
							</li>
							<li>
								<Link
									to="/faq"
									className="text-slate-300 hover:text-cyan-400 transition-colors duration-200"
								>
									שאלות נפוצות
								</Link>
							</li>
						</ul>
					</div>
				</div>

				{/* Newsletter Signup */}
				<div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-lg p-8 mb-12 border border-cyan-500/20">
					<div className="text-center">
						<h3 className="text-white font-semibold text-lg mb-2">
							הישאר מעודכן
						</h3>
						<p className="text-slate-300 text-sm mb-4">
							קבלו עדכונים על מדריכים חדשים וחדשות מהקהילה
						</p>
						<form
							onSubmit={handleNewsletterSubmit}
							className="flex max-w-md mx-auto"
						>
							<input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="כתובת האימייל שלך"
								className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-r-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none text-white placeholder-slate-400 -ml-px"
								required
							/>
							<button
								type="submit"
								disabled={isSubmitting}
								className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-l-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{isSubmitting ? "מרשם..." : "הרשם"}
							</button>
						</form>
						{subscriptionStatus === "success" && (
							<p className="text-green-400 text-sm mt-2">
								נרשמת בהצלחה לניוזלטר!
							</p>
						)}
						{subscriptionStatus === "error" && (
							<p className="text-red-400 text-sm mt-2">
								שגיאה בהרשמה. נסו שוב.
							</p>
						)}
					</div>
				</div>

				{/* Bottom Footer */}
				<div className="border-t border-slate-700/50 pt-10">
					<div className="flex flex-col lg:flex-row justify-between items-center space-y-8 lg:space-y-0">
						<div className="flex flex-col lg:flex-row items-center space-y-6 lg:space-y-0 lg:space-x-32 text-sm text-slate-400">
							<p className="text-center lg:text-right">
								&copy; {currentYear} תיקנתי. כל הזכויות שמורות.
							</p>
							<div className="flex items-center gap-16">
								<Link
									to="/terms"
									className="hover:text-cyan-400 transition-colors duration-200 font-medium"
								>
									תנאי שימוש
								</Link>

								<Link
									to="/privacy"
									className="hover:text-cyan-400 transition-colors duration-200 font-medium"
								>
									מדיניות פרטיות
								</Link>
							</div>
						</div>
						<div className="flex items-center space-x-3 text-slate-400">
							<span className="text-sm">נבנה ב</span>
							<HeartIcon className="w-4 h-4 text-red-400 animate-pulse" />
							<span className="text-sm">למען קהילת ה-DIY בישראל</span>
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
